import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Trash2, CloudUpload, Download, CloudOff } from 'lucide-react';
import { backupStore } from '../../stores/backup.store';
import { serverStore } from '../../stores/server.store';
import { Button } from '../design/Button';
import { SaveBar } from '../server/settings/SaveBar';
import { BackupStatusStrip } from '../server/backups/BackupStatusStrip';
import { BackupProgressCard } from '../server/backups/BackupProgressCard';
import { BackupTimeline } from '../server/backups/BackupTimeline';
import { BackupSchedule } from '../server/backups/BackupSchedule';
import { ManualBackupMenu } from '../server/backups/ManualBackupMenu';
import type { TimelineItem } from '../server/backups/types';
import { backupKind, nextBackupAt, parseDeDate, sizeMb } from '../../utils/backup-format';
import { can } from '@/utils/permissions';

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Backups eines Servers (2026-09-14 neu): oben „Wann zuletzt / wann als Nächstes /
 * wie viel", darunter die Zeitleiste mit „Wiederherstellen" per Klick, unten der
 * Zeitplan in Alltagssprache mit derselben Speichern-Leiste wie die Einstellungen.
 */
export const CloudBackupTab = observer(({ currentUser }: { currentUser: any }) => {
  const { activeServer } = serverStore;
  const allowed = can(currentUser, 'can_manage_backups');
  const base: Record<string, any> = activeServer?.backup_config || {};
  const baseKey = JSON.stringify(base);
  const baseRef = useRef(base);
  const [draft, setDraft] = useState<Record<string, any>>(base);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!activeServer || !allowed) return;
    backupStore.loadDriveBackups(activeServer.id);
    backupStore.loadLocalBackups(activeServer.id);
    backupStore.clearSelection();
  }, [activeServer?.id, allowed]);

  // Frische Serverdaten übernehmen – aber nur in Feldern, die hier niemand geändert hat.
  useEffect(() => {
    const next = JSON.parse(baseKey);
    const prevBase = baseRef.current;
    setDraft((prev) => {
      const merged: Record<string, any> = { ...prev };
      for (const k of new Set([...Object.keys(next), ...Object.keys(prevBase)])) {
        if (same(prev[k], prevBase[k])) merged[k] = next[k];
      }
      return merged;
    });
    baseRef.current = next;
  }, [baseKey]);

  // Cloud nur aus der echten Drive-Liste (nicht aus server.stored_backups – das waren die „Phantome").
  const driveList = activeServer ? backupStore.driveBackups[activeServer.id] : undefined;

  const items: TimelineItem[] = useMemo(() => {
    const local = backupStore.localBackups.map((b: any) => ({
      key: `local:${b.name}`, ms: b.id || parseDeDate(b.date) || 0, kind: backupKind(b.type), isLocal: true,
      name: b.name, size: b.size, path: b.path, id: b.id, type: b.type, date: b.date,
    }));
    const cloud = (driveList || []).filter((b: any) => b.driveId).map((b: any) => ({
      key: `cloud:${b.driveId}`, ms: b.dateMs || parseDeDate(b.date) || 0, kind: backupKind(b.type), isLocal: false,
      name: b.name, size: b.size, driveId: b.driveId, id: b.id, type: b.type, date: b.date,
    }));
    return [...local, ...cloud].sort((a, b) => b.ms - a.ms);
  }, [backupStore.localBackups, driveList]);

  if (!activeServer || !allowed) return null;

  const driveLoading = driveList === undefined || !!backupStore.driveLoading[activeServer.id];
  const driveError = !!backupStore.driveError[activeServer.id];
  const dirtyKeys = Object.keys({ ...draft, ...base }).filter((k) => !same(draft[k], base[k]));
  const local = items.filter((i) => i.isLocal);
  const cloud = items.filter((i) => !i.isLocal);
  const restoreDisabled = backupStore.restoreStatus !== 'idle';

  const save = async () => {
    setSaving(true);
    await serverStore.saveBackupConfig(activeServer.id, draft);
    setSaving(false);
  };
  const selected = Array.from(backupStore.selectedBackups);

  return (
    <div className="space-y-6 max-w-6xl animate-in fade-in duration-300">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-fg tracking-tight">Backups</h2>
          <p className="text-sm text-fg-muted mt-1">
            Sicherungen von {activeServer.name} – auf der Festplatte (HDD) und in Google Drive.
          </p>
        </div>
        <ManualBackupMenu ignoredFiles={draft.ignored_files} />
      </div>

      <BackupProgressCard serverId={activeServer.id} />

      <BackupStatusStrip
        last={items[0] || null}
        nextMs={nextBackupAt(base)}
        online={activeServer.status === 'online'}
        hddCount={local.length}
        hddMb={local.reduce((a, i) => a + sizeMb(i.size), 0)}
        cloudCount={cloud.length}
        cloudMb={cloud.reduce((a, i) => a + sizeMb(i.size), 0)}
      />

      {driveError && !driveLoading && (
        <div className="flex items-center gap-2.5 rounded-xl border border-warn/25 bg-warn/10 px-4 py-2.5 text-sm text-warn">
          <CloudOff size={16} className="shrink-0" />
          Google Drive ist gerade nicht erreichbar – Cloud-Sicherungen fehlen deshalb in der Liste.
          <button onClick={() => backupStore.loadDriveBackups(activeServer.id)} className="ml-auto shrink-0 font-medium text-fg hover:text-ember">
            Erneut versuchen
          </button>
        </div>
      )}

      <BackupTimeline items={items} restoreDisabled={restoreDisabled} loading={backupStore.localBackupsLoading || driveLoading} />

      <BackupSchedule config={draft} serverName={activeServer.name} onChange={(k, v) => setDraft((d) => ({ ...d, [k]: v }))} />

      <SaveBar
        dirtyCount={dirtyKeys.length}
        saving={saving}
        needsRestart={false}
        canRestart={false}
        restartPending={false}
        onSave={save}
        onSaveRestart={save}
        onDiscard={() => setDraft(base)}
        onRestartNow={() => {}}
        onDismissRestart={() => {}}
      />

      {selected.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex flex-wrap items-center gap-3 rounded-xl border border-ember/30 bg-elevated/95 backdrop-blur px-4 py-2.5 shadow-2xl shadow-black/60 animate-in slide-in-from-bottom-5">
          <span className="text-sm text-fg"><span className="font-mono text-ember">{selected.length}</span> ausgewählt</span>
          <Button variant="ghost" size="sm" onClick={() => backupStore.clearSelection()}>Abbrechen</Button>
          {selected.some((k) => k.startsWith('local:')) && (
            <Button variant="secondary" size="sm" onClick={() => backupStore.transferSelectedBackups('to-cloud')}>
              <CloudUpload size={14} /> In die Cloud
            </Button>
          )}
          {selected.some((k) => k.startsWith('cloud:')) && (
            <Button variant="secondary" size="sm" onClick={() => backupStore.transferSelectedBackups('to-local')}>
              <Download size={14} /> Auf die HDD
            </Button>
          )}
          <Button variant="danger" size="sm" onClick={() => backupStore.deleteSelectedBackups()}>
            <Trash2 size={14} /> Löschen
          </Button>
        </div>
      )}
    </div>
  );
});
